import { useState, useMemo } from "react";
import { Link } from "@/components/LangLink";
import { Ruler, CalendarCheck, Info, ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MEASURE_POINTS, POINT_ORDER, SIZE_COLUMNS, CIRCUMFERENCE_TABLE, EXECUTION_META, visiblePoints } from "@/lib/schiebler";

const NAVY = "#1B3A6B";
const GREEN = "#2E8B6B";
const FAINT = "#C9D6E4";

const POINT_MAP = Object.fromEntries(MEASURE_POINTS.map((p) => [p.id, p]));

/* ------------------------------------------------------------------ */
/* Schema gamba (SVG interno, ridisegnato in stile MEDISISTEM)        */
/* ------------------------------------------------------------------ */
const GEO = {
  cG: { y: 80, hw: 60 },
  cF: { y: 152, hw: 52 },
  cE: { y: 214, hw: 45 },
  cD: { y: 262, hw: 40 },
  cC: { y: 330, hw: 46 },
  cB1: { y: 398, hw: 26 },
  cB: { y: 420, hw: 23 },
  cY: { y: 450, hw: 26 },
  cA: { y: 476, hw: 31 },
};
const CX = 175;
const ORDER_TB = ["cG", "cF", "cE", "cD", "cC", "cB1", "cB", "cY", "cA"]; // top → bottom

const legPath = () => {
  let d = `M ${CX - 60} 48`;
  ORDER_TB.forEach((id) => { d += ` L ${CX - GEO[id].hw} ${GEO[id].y}`; });
  // piede verso sinistra
  d += ` L 144 494 L 132 502 C 112 508 98 508 94 512 C 90 515 93 519 100 519 L 206 519 C 214 519 218 512 214 504 L 208 494 L ${CX + GEO.cA.hw} 476`;
  [...ORDER_TB].reverse().slice(1).forEach((id) => { d += ` L ${CX + GEO[id].hw} ${GEO[id].y}`; });
  d += ` L ${CX + 60} 48 Z`;
  return d;
};

export const LegSchematic = ({ upTo = "cG", side = "single", testid = "leg-schematic" }) => {
  const points = visiblePoints(upTo);
  const maxY = GEO[upTo]?.y ?? 80;
  const path = useMemo(legPath, []);
  const clipId = `cov-${upTo}`;

  return (
    <svg viewBox="0 0 300 540" className="h-auto w-full max-w-[300px]" role="img" data-testid={testid}
      aria-label={`Schema gamba con punti di misurazione fino a ${upTo}`}>
      <defs>
        <clipPath id={clipId}><rect x="0" y={maxY} width="300" height={540 - maxY} /></clipPath>
      </defs>
      {/* contorno gamba */}
      <path d={path} fill="#FFFFFF" stroke={FAINT} strokeWidth="2" />
      {/* copertura calza (fino a upTo) */}
      <path d={path} fill={GREEN} fillOpacity="0.12" stroke={GREEN} strokeWidth="2" clipPath={`url(#${clipId})`} />

      {/* linee di misurazione (solo punti visibili) */}
      {points.map((id) => {
        const g = GEO[id];
        const key = POINT_MAP[id]?.key;
        const stroke = key ? GREEN : NAVY;
        const x1 = CX - g.hw - 16;
        const x2 = CX + g.hw + 16;
        return (
          <g key={id} data-testid={`leg-point-${id}`}>
            <line x1={x1} y1={g.y} x2={x2} y2={g.y} stroke={stroke} strokeWidth={key ? 3 : 2} strokeLinecap="round" />
            <path d={`M ${x2 - 7} ${g.y - 5} l 6 5 l -6 5`} fill="none" stroke={stroke} strokeWidth={key ? 3 : 2} strokeLinecap="round" strokeLinejoin="round" />
            <text x={x1 - 8} y={g.y + 4} textAnchor="end" fontSize={key ? 17 : 14} fontWeight={key ? 700 : 500} fill={stroke}>{id}</text>
          </g>
        );
      })}
    </svg>
  );
};

/* ------------------------------------------------------------------ */
/* Tabella circonferenze                                              */
/* ------------------------------------------------------------------ */
const Grid = ({ columns, rows, selected, onSelect, testid }) => (
  <table className="w-full table-fixed border-collapse" data-testid={testid}>
    <colgroup>
      <col style={{ width: "82px" }} />
      {columns.map((c) => (<col key={c} />))}
    </colgroup>
    <thead>
      <tr>
        <th scope="col" className="bg-navy text-white text-left text-[10px] sm:text-xs font-semibold uppercase tracking-wide px-2 py-2.5 rounded-tl-lg">
          Circonf. (cm)
        </th>
        {columns.map((c) => {
          const on = c === selected;
          return (
            <th scope="col" key={c} className="p-0">
              <button
                type="button"
                onClick={() => onSelect(on ? null : c)}
                aria-pressed={on}
                data-testid={`size-col-${c.replace(/\s+/g, "-").toLowerCase()}`}
                className={`w-full px-1 py-2.5 text-center text-[10px] sm:text-xs font-bold uppercase tracking-tight leading-tight transition-colors ${on ? "bg-green text-white" : "bg-navy/5 text-navy hover:bg-green/20"}`}
              >
                {c}
              </button>
            </th>
          );
        })}
      </tr>
    </thead>
    <tbody>
      {rows.map((pid, ri) => (
        <tr key={pid} className={ri % 2 ? "bg-cloud" : "bg-white"}>
          <th scope="row" className={`text-left text-xs font-semibold px-2 py-2 ${POINT_MAP[pid]?.key ? "text-green-deep" : "text-navy"}`}>
            {pid}
          </th>
          {columns.map((c) => {
            const idx = SIZE_COLUMNS.indexOf(c);
            const on = c === selected;
            return (
              <td key={c} className={`px-1 py-2 text-center text-[11px] sm:text-sm tabular-nums ${on ? "bg-green/12 font-semibold text-green-deep" : "text-navy/75"}`}>
                {CIRCUMFERENCE_TABLE[pid]?.[idx]?.replace("-", "–")}
              </td>
            );
          })}
        </tr>
      ))}
    </tbody>
  </table>
);

export const CircumferenceTable = ({ points }) => {
  // righe ordinate dall'alto (coscia) al basso (caviglia), come nella tabella ufficiale
  const rows = [...POINT_ORDER].reverse().filter((p) => points.includes(p));
  const [selected, setSelected] = useState(null);
  const [showPlus, setShowPlus] = useState(false);
  const columns = SIZE_COLUMNS.filter((c) => (showPlus ? c.includes("Plus") : !c.includes("Plus")));
  const toggle = (plus) => { setShowPlus(plus); setSelected(null); };

  return (
    <div data-testid="circumference-table">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-full border border-[#E7EDF2] p-1" role="group" aria-label="Taglie standard o Plus">
          <button type="button" onClick={() => toggle(false)} aria-pressed={!showPlus} data-testid="size-toggle-standard"
            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${!showPlus ? "bg-navy text-white" : "text-navy/70"}`}>Taglie standard</button>
          <button type="button" onClick={() => toggle(true)} aria-pressed={showPlus} data-testid="size-toggle-plus"
            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${showPlus ? "bg-navy text-white" : "text-navy/70"}`}>Taglie Plus</button>
        </div>
        {selected && (
          <span className="inline-flex items-center gap-2 rounded-full bg-green/10 px-4 py-1.5 text-sm font-semibold text-green-deep" data-testid="size-consulted">
            Taglia consultata: {selected}
          </span>
        )}
      </div>

      <div className="overflow-hidden rounded-xl border border-[#E7EDF2]">
        <Grid columns={columns} rows={rows} selected={selected} onSelect={setSelected} testid="size-grid" />
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Tocca una taglia in alto per evidenziare la colonna.</p>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Guida completa (accordion a 2 passaggi)                            */
/* ------------------------------------------------------------------ */
const KEY_POINTS = MEASURE_POINTS.filter((p) => p.key);

const CoverageCaption = ({ side }) => {
  const text =
    side === "both" ? "Copertura: entrambe le gambe"
    : side === "right" ? "Copertura: gamba destra"
    : side === "left" ? "Copertura: gamba sinistra"
    : null;
  if (!text) return null;
  return <span className="mt-2 inline-flex items-center rounded-full bg-navy/5 px-3 py-1 text-xs font-semibold text-navy" data-testid="coverage-caption">{text}</span>;
};

export const SizeGuide = ({ product, execution, onAskSize }) => {
  const meta = EXECUTION_META[execution] || {};
  const upTo = meta.upTo || "cG";
  const side = meta.leg || "single";
  const points = visiblePoints(upTo);
  const madeToMeasure = product.madeToMeasure;

  return (
    <AccordionItem value="guida-misure" data-testid="size-guide">
      <AccordionTrigger data-testid="panel-guida-misure" className="text-navy">
        <span className="inline-flex items-center gap-2"><Ruler size={17} className="text-green" /> Come individuare la misura corretta</span>
      </AccordionTrigger>
      <AccordionContent>
        <div className="grid gap-8 lg:grid-cols-2">
          {/* PASSAGGIO 1 — DOVE MISURARE */}
          <div data-testid="size-step-1">
            <div className="text-[11px] font-bold uppercase tracking-wider text-green-deep">Passaggio 1 — Dove misurare</div>
            <p className="mt-2 text-sm text-muted-foreground">
              Lo schema mostra i punti interessati dall&apos;esecuzione selezionata
              {meta.label ? <> (<span className="font-semibold text-navy">{meta.label}</span>)</> : null}.
            </p>
            <div className="mt-4 flex flex-col items-center rounded-2xl border border-[#E7EDF2] bg-cloud p-4">
              <LegSchematic upTo={upTo} side={side} />
              <CoverageCaption side={side} />
            </div>
            <dl className="mt-4 space-y-1.5">
              {KEY_POINTS.filter((p) => points.includes(p.id)).map((p) => (
                <div key={p.id} className="flex gap-2 text-sm">
                  <dt className="font-bold text-green-deep">{p.id}</dt>
                  <dd className="text-navy/75">{p.desc}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* PASSAGGIO 2 — CONSULTA LA TABELLA (o su misura) */}
          <div data-testid="size-step-2">
            <div className="text-[11px] font-bold uppercase tracking-wider text-green-deep">Passaggio 2 — Consulta la tabella</div>

            {madeToMeasure && (
              <p className="mt-3 flex items-start gap-2 rounded-xl border border-green/20 bg-green/5 px-4 py-3 text-sm text-navy/80" data-testid="made-to-measure-note">
                <Info size={16} className="mt-0.5 shrink-0 text-green" />
                <span><span className="font-semibold text-navy">Prodotto realizzato su misura.</span> La tabella è un riferimento delle circonferenze; non determina una taglia standard. La misura definitiva viene stabilita durante la misurazione professionale.</span>
              </p>
            )}
            <p className="mt-3 rounded-xl bg-navy/5 px-4 py-3 text-sm text-navy/80" data-testid="plus-explanation">
              Le taglie Plus sono previste per circonferenze maggiori in determinati punti della gamba. La dicitura Plus non indica una classe di compressione superiore.
            </p>
            <div className="mt-4">
              <CircumferenceTable points={points} />
            </div>
            <p className="mt-3 text-sm text-navy/75">
              Confronta tutte le circonferenze rilevate con gli intervalli presenti nella stessa colonna. La taglia non deve essere determinata utilizzando una sola misura.
            </p>
            <p className="mt-3 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="length-note">
              <Info size={16} className="mt-0.5 shrink-0 text-amber-600" />
              La corretta scelta della calza può richiedere anche la verifica della lunghezza. In caso di dubbi o di misure comprese in colonne differenti, è consigliata una misurazione professionale.
            </p>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              {!madeToMeasure && (
                <button type="button" onClick={onAskSize} data-testid="size-ask-confirm" className="btn-green">
                  Chiedi conferma della taglia <ArrowRight size={16} />
                </button>
              )}
              <Link to="/contatti#prenota" data-testid="size-book-measurement" className={madeToMeasure ? "btn-green" : "inline-flex items-center justify-center gap-2 rounded-full border border-navy/20 px-6 py-3 text-sm font-semibold text-navy transition-colors hover:border-green hover:text-green"}>
                <CalendarCheck size={16} /> Prenota una misurazione
              </Link>
            </div>
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
